import type {
  ChangePasswordRequest,
  IUser,
  PaginatedDTO,
  ResetPasswordResponseDTO,
  UserAccountLevelUpdateRequest,
  UserCreateRequest,
  UserDTO,
  UserListParams,
  UserUpdateRequest,
} from '@/shared/api/types/user-types'
import { mockDelay, mockUsers } from './__mocks__/iam-fixtures'

// TODO(real-api): swap mock array reads for `request.get/post/patch` calls
// against /api/users. Function signatures stay identical.

function assertMockIamAllowed(): void {
  if (!import.meta.env.DEV) {
    throw new Error('user-service: mock IAM data is only available in local development')
  }
}

function clone(user: UserDTO): UserDTO {
  return { ...user }
}

function matchesSearch(user: UserDTO, term: string): boolean {
  const keyword = term.trim().toLowerCase()
  if (!keyword) {
    return true
  }
  return (
    user.username.toLowerCase().includes(keyword) ||
    (user.email ?? '').toLowerCase().includes(keyword) ||
    (user.fullName ?? '').toLowerCase().includes(keyword)
  )
}

export async function getUsers(params: UserListParams): Promise<PaginatedDTO<IUser>> {
  assertMockIamAllowed()
  await mockDelay()
  const filtered = mockUsers
    .filter((user) => matchesSearch(user, params.search ?? ''))
    .filter((user) => (params.accountLevel ? user.accountLevel === params.accountLevel : true))
    .filter((user) => (params.isActive === undefined ? true : user.isActive === params.isActive))

  const items = filtered.slice(params.skip, params.skip + params.limit).map(clone)
  return {
    items,
    total: filtered.length,
    skip: params.skip,
    limit: params.limit,
  }
}

export async function getUserById(id: string): Promise<IUser> {
  assertMockIamAllowed()
  await mockDelay(120)
  const user = mockUsers.find((u) => u.id === id)
  if (!user) {
    throw new Error(`User ${id} not found`)
  }
  return clone(user)
}

export async function getCurrentUser(): Promise<IUser> {
  assertMockIamAllowed()
  await mockDelay(80)
  // First super-admin in the fixture set acts as the "me" stub.
  const me = mockUsers.find((u) => u.accountLevel === 'SUPER_ADMIN') ?? mockUsers[0]
  if (!me) {
    throw new Error('No mock user available for getCurrentUser')
  }
  return clone(me)
}

export async function createUser(payload: UserCreateRequest): Promise<IUser> {
  assertMockIamAllowed()
  await mockDelay()
  const user: UserDTO = {
    id: `u-${crypto.randomUUID().slice(0, 8)}`,
    tenantId: null,
    username: payload.username,
    email: payload.email ?? null,
    fullName: payload.fullName ?? null,
    avatarUrl: payload.avatarUrl ?? null,
    phone: payload.phone ?? null,
    accountLevel: 'USER',
    isActive: true,
    createdAt: new Date().toISOString(),
  }
  mockUsers.unshift(user)
  return clone(user)
}

export async function updateUser(id: string, payload: UserUpdateRequest): Promise<IUser> {
  assertMockIamAllowed()
  await mockDelay()
  const user = mockUsers.find((u) => u.id === id)
  if (!user) {
    throw new Error(`User ${id} not found`)
  }
  if (payload.fullName !== undefined) user.fullName = payload.fullName
  if (payload.email !== undefined) user.email = payload.email
  if (payload.avatarUrl !== undefined) user.avatarUrl = payload.avatarUrl
  if (payload.phone !== undefined) user.phone = payload.phone
  return clone(user)
}

export async function updateUserAccountLevel(
  id: string,
  payload: UserAccountLevelUpdateRequest,
): Promise<IUser> {
  assertMockIamAllowed()
  await mockDelay()
  const user = mockUsers.find((u) => u.id === id)
  if (!user) {
    throw new Error(`User ${id} not found`)
  }
  user.accountLevel = payload.accountLevel
  return clone(user)
}

export async function changeUserPassword(
  id: string,
  payload: ChangePasswordRequest,
): Promise<void> {
  assertMockIamAllowed()
  await mockDelay()
  const user = mockUsers.find((u) => u.id === id)
  if (!user) {
    throw new Error(`User ${id} not found`)
  }
  // Mock — no password stored. payload.newPassword length validated in form.
  void payload
}

export async function resetUserPassword(id: string): Promise<ResetPasswordResponseDTO> {
  assertMockIamAllowed()
  await mockDelay()
  const user = mockUsers.find((u) => u.id === id)
  if (!user) {
    throw new Error(`User ${id} not found`)
  }
  return {
    userId: user.id,
    temporaryPassword: generateTempPassword(),
  }
}

// TODO(real-api): the spec has no toggle endpoint — this mock toggles locally
// and the real implementation will need a new endpoint or PATCH /users/{id}.
export async function toggleUserStatus(id: string): Promise<IUser> {
  assertMockIamAllowed()
  await mockDelay()
  const user = mockUsers.find((u) => u.id === id)
  if (!user) {
    throw new Error(`User ${id} not found`)
  }
  user.isActive = !user.isActive
  return clone(user)
}

function generateTempPassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  let out = ''
  for (let i = 0; i < 12; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}
